export interface SavedLoginCredentials {
  phone: string;
  pass: string;
}

interface EncryptedCredentials {
  version: 1;
  iv: ArrayBuffer;
  data: ArrayBuffer;
}

const DATABASE_NAME = "khm-secure-storage";
const DATABASE_VERSION = 1;
const STORE_NAME = "vault";
const KEY_RECORD = "login-key";
const CREDENTIAL_RECORD = "login-credentials";

const requestResult = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionDone = (transaction: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

const openDatabase = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

async function readRecord<T>(record: string): Promise<T | undefined> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readonly");
    return await requestResult<T | undefined>(
      transaction.objectStore(STORE_NAME).get(record),
    );
  } finally {
    database.close();
  }
}

async function writeRecord(record: string, value: unknown) {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(value, record);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

async function deleteRecord(record: string) {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).delete(record);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

async function getEncryptionKey() {
  const existing = await readRecord<CryptoKey>(KEY_RECORD);
  if (existing) return existing;

  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  await writeRecord(KEY_RECORD, key);
  return key;
}

const storageAvailable = () =>
  typeof indexedDB !== "undefined" && Boolean(globalThis.crypto?.subtle);

export async function saveLoginCredentials(
  credentials: SavedLoginCredentials,
) {
  const phone = credentials.phone.replace(/\D/g, "");
  if (!storageAvailable() || !phone || !credentials.pass) return;

  const key = await getEncryptionKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(
    JSON.stringify({ phone, pass: credentials.pass }),
  );
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);
  const encrypted: EncryptedCredentials = {
    version: 1,
    iv: iv.buffer,
    data,
  };
  await writeRecord(CREDENTIAL_RECORD, encrypted);
}

export async function loadLoginCredentials(): Promise<SavedLoginCredentials | null> {
  if (!storageAvailable()) return null;

  try {
    const encrypted = await readRecord<EncryptedCredentials>(CREDENTIAL_RECORD);
    const key = await readRecord<CryptoKey>(KEY_RECORD);
    if (!encrypted || encrypted.version !== 1 || !key) return null;

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(encrypted.iv) },
      key,
      encrypted.data,
    );
    const credentials = JSON.parse(
      new TextDecoder().decode(decrypted),
    ) as Partial<SavedLoginCredentials>;
    if (typeof credentials.phone !== "string" || typeof credentials.pass !== "string") {
      throw new Error("Invalid saved credentials");
    }
    return { phone: credentials.phone, pass: credentials.pass };
  } catch {
    await deleteRecord(CREDENTIAL_RECORD).catch(() => undefined);
    return null;
  }
}
